import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button, Card, Col, Empty, Input, Modal, Popconfirm, Radio, Row, Space, Spin, Steps, Typography, message,
} from 'antd';
import {
    EditOutlined, PlusCircleFilled, ArrowUpOutlined, ArrowDownOutlined,
    DownloadOutlined, UploadOutlined, CopyOutlined, DeleteOutlined, ThunderboltOutlined, SaveOutlined,
    TagsOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    listReportDashboardsByUser,
    createReportDashboard,
    patchReportDashboard,
    deleteReportDashboard,
    listDataSources,
    ensureDefaultDataSources,
    ensureDefaultCategoryLabelMaps,
    listReportFunctions,
    loadDefaultDashboard,
} from '../../../actions/reportingActions';
import CategoryLabelMapsDrawer from './CategoryLabelMapsDrawer';
import { cloneDashboardForCreate } from './reportingChartUtils';
import DashboardEditorDrawer from './DashboardEditorDrawer';
import DashboardChartsView from './DashboardChartsView';
import SortableDashboardSections from './SortableDashboardSections';
import { layoutItemsEqual } from './dashboardLayoutItems';
import { reorderByIndex } from './dashboardSectionOrder';
import { IconReload } from './reportingIcons';
import { ifAccessDenied } from '../../../utils/formatApiError';
import ApiErrorAlert from '../../common/ApiErrorAlert';
import {
    buildDashboardExportBundle,
    parseDashboardImportBundle,
    planDashboardImport,
    downloadJsonFile,
} from './dashboardPortability';

const { Text } = Typography;

const UPLOAD_BATCHES_DASHBOARD_NAME = 'Upload batches';

function sortDashboards(list) {
    return [...list].sort(
        (a, b) => {
            const aPinned = a.name === UPLOAD_BATCHES_DASHBOARD_NAME ? -1 : 0;
            const bPinned = b.name === UPLOAD_BATCHES_DASHBOARD_NAME ? -1 : 0;
            if (aPinned !== bPinned) return aPinned - bPinned;
            return (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
                || String(a.name ?? '').localeCompare(String(b.name ?? ''));
        },
    );
}

export default function ReportingDashboardPanel({
    applicationUserId, dateRange, globalFilters = {}, active = true,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { items: dashboards = [], loading, error } = useSelector(s => s.reportDashboardList);
    const { items: functions = [] } = useSelector(s => s.reportFunctionList ?? { items: [] });
    const importInputRef = useRef(null);

    const [activeDashboard, setActiveDashboard] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const [localDashboards, setLocalDashboards] = useState([]);
    const [layoutDrafts, setLayoutDrafts] = useState({});
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const [savingChanges, setSavingChanges] = useState(false);
    const [layoutResetKey, setLayoutResetKey] = useState(0);
    const [editingNameId, setEditingNameId] = useState(null);
    const [nameDraft, setNameDraft] = useState('');
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [sectionRefreshKeys, setSectionRefreshKeys] = useState({});
    const [loadDefaultsOpen, setLoadDefaultsOpen] = useState(false);
    const [loadDefaultsLoading, setLoadDefaultsLoading] = useState(false);
    const [loadDefaultsStep, setLoadDefaultsStep] = useState(0);
    const [loadDefaultsMessage, setLoadDefaultsMessage] = useState('');
    const [loadDefaultsProgress, setLoadDefaultsProgress] = useState({ current: 0, total: 0 });
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importDuplicatePolicy, setImportDuplicatePolicy] = useState('skip');
    const [importPending, setImportPending] = useState(null);
    const [importExecuting, setImportExecuting] = useState(false);
    const [labelMapsOpen, setLabelMapsOpen] = useState(false);

    const dashboardId = (db) => db?.id ?? db?.Id;

    const refresh = useCallback(() => {
        if (!applicationUserId) return;
        dispatch(ensureDefaultDataSources());
        dispatch(ensureDefaultCategoryLabelMaps());
        dispatch(listReportDashboardsByUser(applicationUserId));
        dispatch(listDataSources());
        dispatch(listReportFunctions());
    }, [dispatch, applicationUserId]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (!active) setExpandedKeys([]);
    }, [active]);

    useEffect(() => {
        if (!hasPendingChanges) {
            setLocalDashboards(sortDashboards(dashboards));
            setLayoutDrafts({});
        }
    }, [dashboards, hasPendingChanges]);

    const openEditor = (dashboard) => {
        setActiveDashboard(dashboard);
        setEditorOpen(true);
    };

    const nextDisplayOrder = () => {
        if (dashboards.length === 0) return 0;
        return Math.max(...dashboards.map(d => d.displayOrder ?? 0)) + 1;
    };

    const moveDashboard = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= localDashboards.length) return;
        setLocalDashboards(prev => reorderByIndex(prev, index, target));
        setHasPendingChanges(true);
    };

    const reorderDashboards = useCallback((fromIndex, toIndex) => {
        setLocalDashboards(prev => reorderByIndex(prev, fromIndex, toIndex));
        setHasPendingChanges(true);
    }, []);

    const handleRefreshSection = useCallback((db) => {
        const id = dashboardId(db);
        if (!id) return;
        setExpandedKeys(prev => (prev.includes(id) ? prev : [...prev, id]));
        setSectionRefreshKeys(prev => ({
            ...prev,
            [id]: (prev[id] ?? 0) + 1,
        }));
    }, []);

    const applyLayoutDraft = useCallback((db) => {
        const id = dashboardId(db);
        const draftCharts = layoutDrafts[id];
        if (!draftCharts) return db;
        return {
            ...db,
            layoutJson: { ...(db.layoutJson ?? {}), charts: draftCharts },
        };
    }, [layoutDrafts]);

    const handleLayoutDraft = useCallback((dashId, items) => {
        setLayoutDrafts(prev => {
            const existing = prev[dashId];
            if (existing && layoutItemsEqual(existing, items)) return prev;
            return { ...prev, [dashId]: items };
        });
        setHasPendingChanges(true);
    }, []);

    const handleSaveChanges = async () => {
        if (!hasPendingChanges || localDashboards.length === 0) return;
        setSavingChanges(true);
        try {
            const serverSorted = sortDashboards(dashboards);
            const orderChanged = localDashboards.some(
                (db, i) => dashboardId(db) !== dashboardId(serverSorted[i]),
            );

            for (let i = 0; i < localDashboards.length; i++) {
                const db = localDashboards[i];
                const id = dashboardId(db);
                if (!id) continue;

                const patch = {};
                if (orderChanged) patch.displayOrder = i;

                const draftCharts = layoutDrafts[id];
                if (draftCharts) {
                    const { filters: _f, ...layoutRest } = db.layoutJson ?? {};
                    patch.layoutJson = { ...layoutRest, charts: draftCharts };
                }

                if (Object.keys(patch).length > 0) {
                    await dispatch(patchReportDashboard(id, patch));
                }
            }

            setHasPendingChanges(false);
            setLayoutDrafts({});
            setLayoutResetKey(k => k + 1);
            message.success(t('Changes saved'));
        } finally {
            setSavingChanges(false);
        }
    };

    const handleExportDashboards = () => {
        if (dashboards.length === 0) {
            message.warning(t('No dashboards to export'));
            return;
        }
        const bundle = buildDashboardExportBundle(dashboards, functions);
        const stamp = new Date().toISOString().slice(0, 10);
        downloadJsonFile(`wessex-reporting-dashboards-${stamp}.json`, bundle);
        message.success(t('Exported {{count}} dashboard(s)', { count: dashboards.length }));
    };

    const runDashboardImport = async (importedDashboards, duplicatePolicy, parseWarnings = []) => {
        const { toCreate, toOverride, skipped } = planDashboardImport(
            importedDashboards,
            dashboards,
            duplicatePolicy,
        );
        let order = nextDisplayOrder();
        let created = 0;
        let updated = 0;

        for (const { imported, existing } of toOverride) {
            const id = dashboardId(existing);
            const result = await dispatch(patchReportDashboard(id, {
                name: imported.name,
                description: imported.description,
                layoutJson: imported.layoutJson,
            }));
            if (result) updated += 1;
        }

        for (const db of toCreate) {
            const result = await dispatch(createReportDashboard({
                applicationUserId,
                name: db.name,
                description: db.description,
                layoutJson: db.layoutJson,
                isActive: true,
                displayOrder: order++,
            }));
            if (result) created += 1;
        }

        await dispatch(listReportDashboardsByUser(applicationUserId));
        setHasPendingChanges(false);

        const allWarnings = [...parseWarnings];
        if (skipped > 0) {
            allWarnings.push(t('Skipped {{count}} dashboard(s) with duplicate names', { count: skipped }));
        }

        if (allWarnings.length > 0) {
            Modal.warning({
                title: t('Import completed with warnings'),
                content: (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {allWarnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                ),
                width: 520,
            });
        }

        if (created > 0 || updated > 0) {
            message.success(
                t('Import complete: {{created}} created, {{updated}} updated', { created, updated }),
            );
        } else {
            message.info(t('No dashboards were imported'));
        }
    };

    const handleImportFile = async (file) => {
        if (!file) return;
        try {
            const text = await file.text();
            const bundle = JSON.parse(text);
            const { dashboards: imported, warnings } = parseDashboardImportBundle(bundle, functions);
            if (imported.length === 0) {
                message.error(t('No dashboards found in file'));
                return;
            }

            const { duplicates } = planDashboardImport(imported, dashboards, 'skip');
            if (duplicates.length > 0) {
                setImportPending({ imported, warnings, duplicates });
                setImportDuplicatePolicy('skip');
                setImportModalOpen(true);
                return;
            }

            await runDashboardImport(imported, 'skip', warnings);
        } catch (e) {
            message.error(e.message || t('Invalid dashboard export file'));
        } finally {
            if (importInputRef.current) importInputRef.current.value = '';
        }
    };

    const handleConfirmImport = async () => {
        if (!importPending) return;
        setImportExecuting(true);
        try {
            await runDashboardImport(
                importPending.imported,
                importDuplicatePolicy,
                importPending.warnings,
            );
            setImportModalOpen(false);
            setImportPending(null);
        } finally {
            setImportExecuting(false);
            if (importInputRef.current) importInputRef.current.value = '';
        }
    };

    const handleDeleteDashboard = async (db) => {
        const id = dashboardId(db);
        if (!id) return;
        const result = await dispatch(deleteReportDashboard(id));
        if (result !== true) {
            ifAccessDenied(result, t);
            return;
        }
        message.success(t('Dashboard deleted'));
        setExpandedKeys(keys => keys.filter(k => k !== id));
        setLocalDashboards(prev => prev.filter(d => dashboardId(d) !== id));
        setLayoutDrafts(prev => {
            const next = { ...prev };
            delete next[String(id)];
            return next;
        });
        if (dashboardId(activeDashboard) === id) {
            setEditorOpen(false);
            setActiveDashboard(null);
        }
    };

    const handleDuplicateDashboard = async (db) => {
        const clone = cloneDashboardForCreate(db);
        const created = await dispatch(createReportDashboard({
            applicationUserId,
            name: clone.name,
            description: db.description,
            layoutJson: clone.layoutJson,
            isActive: true,
            displayOrder: nextDisplayOrder(),
        }));
        if (created) {
            message.success(t('Dashboard duplicated'));
            dispatch(listReportDashboardsByUser(applicationUserId));
        } else {
            message.error(t('Failed to duplicate dashboard'));
        }
    };

    const startRenameDashboard = (db) => {
        setEditingNameId(dashboardId(db));
        setNameDraft(db.name ?? '');
    };

    const commitRenameDashboard = async (db) => {
        const id = dashboardId(db);
        const trimmed = nameDraft.trim();
        setEditingNameId(null);
        if (!trimmed || trimmed === db.name) return;
        const updated = await dispatch(patchReportDashboard(id, { name: trimmed }));
        if (updated) {
            message.success(t('Dashboard renamed'));
            if (dashboardId(activeDashboard) === id) {
                setActiveDashboard(prev => (prev ? { ...prev, name: trimmed } : prev));
            }
            dispatch(listReportDashboardsByUser(applicationUserId));
        } else {
            message.error(t('Failed to rename dashboard'));
        }
    };

    const runLoadDefaults = async (wipe) => {
        setLoadDefaultsOpen(true);
        setLoadDefaultsLoading(true);
        setLoadDefaultsStep(0);
        setLoadDefaultsMessage(t('Starting…'));
        setLoadDefaultsProgress({ current: 0, total: 0 });

        if (wipe) {
            setEditorOpen(false);
            setActiveDashboard(null);
            setExpandedKeys([]);
        }

        const result = await dispatch(loadDefaultDashboard(applicationUserId, {
            wipe,
            onProgress: ({ step, current, total, message: msg }) => {
                const stepIndex = {
                    wipe: 0,
                    datasource: wipe ? 1 : 0,
                    templates: wipe ? 1 : 0,
                    deploy: wipe ? 2 : 1,
                    register: wipe ? 3 : 2,
                    dashboard: wipe ? 4 : 3,
                }[step] ?? 0;
                setLoadDefaultsStep(stepIndex);
                if (msg) setLoadDefaultsMessage(msg);
                if (current != null && total != null) setLoadDefaultsProgress({ current, total });
            },
        }));

        setLoadDefaultsLoading(false);

        if (result?.error) {
            message.error(result.error);
            if (result.warnings?.length) {
                Modal.warning({
                    title: t('Load defaults — warnings'),
                    content: (
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    ),
                });
            }
            return;
        }

        setLoadDefaultsOpen(false);
        setHasPendingChanges(false);
        refresh();
        const created = result.dashboards ?? (result.dashboard ? [result.dashboard] : []);
        if (created.length > 0) {
            setExpandedKeys([dashboardId(created[0])]);
        }
        if (result.deployErrors?.length) {
            Modal.warning({
                title: t('Dashboards created with warnings'),
                content: (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {result.deployErrors.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                ),
            });
        }
        message.success(t('Created {{count}} default dashboard(s)', { count: created.length }));
    };

    const handleLoadDefaults = () => {
        const dashCount = dashboards.length;
        const fnCount = functions.length;

        if (dashCount === 0 && fnCount === 0) {
            runLoadDefaults(false);
            return;
        }

        Modal.confirm({
            title: t('Replace all reporting setup?'),
            okText: t('Replace and load defaults'),
            okType: 'danger',
            cancelText: t('Cancel'),
            width: 480,
            zIndex: 1050,
            content: (
                <div>
                    <p>{t('This will permanently remove your current SQL reporting setup and start over with the default Data Vault dashboards.')}</p>
                    <ul style={{ margin: '12px 0', paddingLeft: 20 }}>
                        <li>{t('{{count}} dashboard(s) and all charts', { count: dashCount })}</li>
                        <li>{t('{{count}} registered report function(s)', { count: fnCount })}</li>
                    </ul>
                    <p style={{ marginBottom: 0 }}>
                        <Text type="secondary">
                            {t('PostgreSQL functions on the database are redeployed from templates; data sources are kept.')}
                        </Text>
                    </p>
                </div>
            ),
            onOk: () => {
                setLoadDefaultsOpen(true);
                runLoadDefaults(true);
            },
        });
    };

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setCreating(true);
        const created = await dispatch(createReportDashboard({
            applicationUserId,
            name: newName.trim(),
            layoutJson: { charts: [] },
            isActive: true,
            displayOrder: nextDisplayOrder(),
        }));
        setCreating(false);
        if (created) {
            setCreateOpen(false);
            setNewName('');
            dispatch(listReportDashboardsByUser(applicationUserId));
            openEditor(created);
        }
    };

    const renderDashboardLabel = (db) => {
        const id = dashboardId(db);
        const chartCount = (db.layoutJson?.charts ?? []).filter(
            c => !c.kind || c.kind === 'chart',
        ).length;
        return (
            <Space wrap onClick={e => e.stopPropagation()} style={{ flex: 1 }}>
                {editingNameId === id ? (
                    <Input
                        size="small"
                        value={nameDraft}
                        onChange={e => setNameDraft(e.target.value)}
                        onBlur={() => commitRenameDashboard(db)}
                        onPressEnter={() => commitRenameDashboard(db)}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                        style={{ width: 200, maxWidth: 'min(200px, 50vw)' }}
                    />
                ) : (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={e => { e.stopPropagation(); startRenameDashboard(db); }}
                        onKeyDown={e => e.key === 'Enter' && startRenameDashboard(db)}
                        style={{ cursor: 'text' }}
                        title={t('Click to rename')}
                    >
                        {db.name}
                    </span>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {chartCount} {chartCount === 1 ? t('chart') : t('charts')}
                </Text>
            </Space>
        );
    };

    const renderDashboardExtra = (db, index) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
            <Button
                type="text"
                size="small"
                icon={<IconReload />}
                onClick={() => handleRefreshSection(db)}
                title={t('Refresh charts')}
            />
            <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0 || savingChanges}
                onClick={() => moveDashboard(index, -1)}
                title={t('Move up')}
            />
            <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === localDashboards.length - 1 || savingChanges}
                onClick={() => moveDashboard(index, 1)}
                title={t('Move down')}
            />
            <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleDuplicateDashboard(db)}
                title={t('Duplicate dashboard')}
            />
            <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditor(db)}
                title={t('Edit dashboard')}
            />
            <Popconfirm
                title={t('Delete this dashboard?')}
                description={t('Charts and layout will be removed. Report functions are not deleted.')}
                onConfirm={() => handleDeleteDashboard(db)}
                okText={t('Delete')}
                okType="danger"
            >
                <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    title={t('Delete dashboard')}
                />
            </Popconfirm>
        </Space>
    );

    const displayDashboards = localDashboards.length > 0 ? localDashboards : sortDashboards(dashboards);

    const toolbar = (
        <Space wrap style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 12 }}>
            {hasPendingChanges && (
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={savingChanges}
                    onClick={handleSaveChanges}
                >
                    {t('Save changes')}
                </Button>
            )}
            <Button
                icon={<TagsOutlined />}
                onClick={() => setLabelMapsOpen(true)}
            >
                {t('Label maps')}
            </Button>
            <Button
                icon={<ThunderboltOutlined />}
                loading={loadDefaultsLoading}
                onClick={handleLoadDefaults}
            >
                {t('Load defaults')}
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExportDashboards}>
                {t('Export dashboards')}
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => importInputRef.current?.click()}>
                {t('Import dashboards')}
            </Button>
            <input
                ref={importInputRef}
                type="file"
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={e => handleImportFile(e.target.files?.[0])}
            />
        </Space>
    );

    if (loading) return <Spin style={{ display: 'block', margin: 32 }} />;
    if (error) return <ApiErrorAlert error={error} />;

    return (
        <>
            {dashboards.length === 0 && !loading ? (
                <Empty
                    description={t('No dashboards yet. Create one to start building charts.')}
                    style={{ padding: 24 }}
                >
                    <Space direction="vertical" size="middle">
                        <Button type="primary" onClick={() => setCreateOpen(true)}>
                            {t('New dashboard')}
                        </Button>
                        <Button
                            type="default"
                            icon={<ThunderboltOutlined />}
                            loading={loadDefaultsLoading}
                            onClick={handleLoadDefaults}
                        >
                            {t('Load defaults')}
                        </Button>
                        <Button icon={<UploadOutlined />} onClick={() => importInputRef.current?.click()}>
                            {t('Import dashboards')}
                        </Button>
                        <input
                            ref={importInputRef}
                            type="file"
                            accept=".json,application/json"
                            style={{ display: 'none' }}
                            onChange={e => handleImportFile(e.target.files?.[0])}
                        />
                    </Space>
                </Empty>
            ) : (
                <div style={{ width: '100%' }}>
                    {toolbar}
                    {displayDashboards.length > 1 && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                            {t('Drag a section by the handle to reorder. Use Save changes when done.')}
                        </Text>
                    )}
                    <SortableDashboardSections
                        dashboards={displayDashboards}
                        getDashboardId={dashboardId}
                        expandedKeys={expandedKeys}
                        onExpandedKeysChange={setExpandedKeys}
                        onReorder={reorderDashboards}
                        reorderDisabled={savingChanges}
                        renderLabel={(db) => renderDashboardLabel(applyLayoutDraft(db))}
                        renderExtra={(db, index) => renderDashboardExtra(applyLayoutDraft(db), index)}
                        renderPanelBody={(db) => {
                            const id = dashboardId(db);
                            const isExpanded = expandedKeys.includes(id);
                            return (
                                <DashboardChartsView
                                    key={`${id}-${layoutResetKey}`}
                                    dashboard={db}
                                    dateRange={dateRange}
                                    globalFilters={globalFilters}
                                    showDatePicker={false}
                                    active={isExpanded}
                                    refreshKey={sectionRefreshKeys[id] ?? 0}
                                    onLayoutDraftChange={(items) => handleLayoutDraft(id, items)}
                                />
                            );
                        }}
                    />
                </div>
            )}

            <Row gutter={[16, 16]} style={{ paddingTop: dashboards.length > 0 ? 8 : 0 }}>
                <Col span={24}>
                    <Card
                        hoverable
                        onClick={() => setCreateOpen(true)}
                        style={{ cursor: 'pointer', border: '2px dashed #d9d9d9', background: 'transparent', width: '100%' }}
                        styles={{
                            body: {
                                minHeight: 72,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            },
                        }}
                    >
                        <Space>
                            <PlusCircleFilled style={{ fontSize: 20, opacity: 0.35 }} />
                            <Text type="secondary">{t('New dashboard')}</Text>
                        </Space>
                    </Card>
                </Col>
            </Row>

            <Modal
                open={importModalOpen}
                title={t('Import dashboards — duplicates')}
                onCancel={() => {
                    if (!importExecuting) {
                        setImportModalOpen(false);
                        setImportPending(null);
                    }
                }}
                onOk={handleConfirmImport}
                confirmLoading={importExecuting}
                okText={t('Import')}
                cancelText={t('Cancel')}
                width={520}
            >
                {importPending && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Text>
                            {t('{{count}} dashboard(s) in the file match existing names:', {
                                count: importPending.duplicates.length,
                            })}
                        </Text>
                        <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 160, overflow: 'auto' }}>
                            {importPending.duplicates.map(({ imported }, i) => (
                                <li key={i}>{imported.name}</li>
                            ))}
                        </ul>
                        <Radio.Group
                            value={importDuplicatePolicy}
                            onChange={e => setImportDuplicatePolicy(e.target.value)}
                        >
                            <Space direction="vertical">
                                <Radio value="skip">
                                    {t('Skip duplicates — import only dashboards with new names')}
                                </Radio>
                                <Radio value="override">
                                    {t('Replace existing — overwrite dashboards that have the same name')}
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Space>
                )}
            </Modal>

            <Modal
                open={loadDefaultsOpen}
                title={t('Load defaults')}
                width={820}
                zIndex={2000}
                mask={{ zIndex: 1999 }}
                footer={loadDefaultsLoading ? null : (
                    <Button onClick={() => setLoadDefaultsOpen(false)}>{t('Close')}</Button>
                )}
                closable={!loadDefaultsLoading}
                maskClosable={!loadDefaultsLoading}
                onCancel={() => { if (!loadDefaultsLoading) setLoadDefaultsOpen(false); }}
            >
                <Steps
                    current={loadDefaultsStep}
                    style={{ marginBottom: 16 }}
                    items={[
                        { title: t('Clear') },
                        { title: t('Data source') },
                        { title: t('Deploy') },
                        { title: t('Register') },
                        { title: t('Dashboard') },
                    ]}
                />
                <Space direction="vertical" style={{ width: '100%' }}>
                    {loadDefaultsLoading && <Spin />}
                    <Text type="secondary">{loadDefaultsMessage}</Text>
                    {loadDefaultsProgress.total > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {loadDefaultsProgress.current} / {loadDefaultsProgress.total}
                        </Text>
                    )}
                </Space>
            </Modal>

            <Modal
                open={createOpen}
                title={t('New dashboard')}
                onCancel={() => { setCreateOpen(false); setNewName(''); }}
                onOk={handleCreate}
                confirmLoading={creating}
                okText={t('Create')}
            >
                <Input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder={t('Dashboard name')}
                    onPressEnter={handleCreate}
                    autoFocus
                />
            </Modal>

            {activeDashboard && (
                <DashboardEditorDrawer
                    open={editorOpen}
                    dashboard={activeDashboard}
                    applicationUserId={applicationUserId}
                    dateRange={dateRange}
                    globalFilters={globalFilters}
                    onDashboardUpdated={(updated) => setActiveDashboard(updated)}
                    onClose={() => {
                        setEditorOpen(false);
                        if (!hasPendingChanges) refresh();
                    }}
                />
            )}

            <CategoryLabelMapsDrawer
                open={labelMapsOpen}
                onClose={() => setLabelMapsOpen(false)}
            />
        </>
    );
}
